/**
 * PI Calculator Section Components
 * Individual accordion sections for the PI Calculator
 * Aligned with CMS 2025 PI measure specifications
 */

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { DateRangePicker, toLocalDateString } from '@/components/ui/date-range-picker';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { AlertTriangle, CheckCircle2, XCircle, Info, ExternalLink, ChevronDown, ChevronRight } from 'lucide-react';
import { YesNoButtonGroup } from '@/components/ui/yes-no-button-group';
import { MipsDataService } from '@/lib/mips-data-service';
import { useProgramYear } from '@/contexts/ProgramYearContext';
import type { PICalculatorData, PIScoreBreakdown } from '@/lib/pi-calculator-utils';

const dataService = MipsDataService.getInstance();

interface SectionProps {
  data: PICalculatorData;
  onChange: (updates: Partial<PICalculatorData>) => void;
  disabled?: boolean;
}

// ============================================================================
// Helper Components
// ============================================================================

/**
 * Clickable measure ID link that opens the CMS measure specification
 */
function MeasureIdLink({ measureId }: { measureId: string }) {
  const specUrl = dataService.getPIMeasureSpecificationUrl(measureId);
  
  if (!specUrl) {
    return <span className="text-primary font-medium">{measureId}</span>;
  }
  
  return (
    <Button
      variant="link"
      className="h-auto p-0 text-primary font-medium hover:text-primary/80"
      onClick={() => window.open(specUrl, '_blank', 'noopener,noreferrer')}
    >
      {measureId}
      <ExternalLink className="w-3 h-3 ml-1" />
    </Button>
  );
}

/**
 * Info box with neutral gray background for help text
 */
function InfoBox({ children }: { children: React.ReactNode }) {
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 text-sm text-slate-700 leading-relaxed">
      {children}
    </div>
  );
}

/**
 * Points Earned footer for sections
 */
function PointsEarnedFooter({ points, maxPoints }: { points: number; maxPoints: number }) {
  return (
    <div className="flex items-center justify-between pt-4 mt-6 border-t border-slate-200 bg-slate-50 -mx-4 px-4 py-4 -mb-4">
      <span className="font-medium text-gray-900">Points Earned</span>
      <span className="text-lg font-semibold text-primary">{points} / {maxPoints}</span>
    </div>
  );
}

/**
 * Numerator/Denominator input with performance rate calculation
 */
function NumeratorDenominatorInput({
  numeratorValue,
  denominatorValue,
  onNumeratorChange,
  onDenominatorChange,
  disabled,
  numeratorLabel = 'Numerator',
  denominatorLabel = 'Denominator',
}: {
  numeratorValue: number | null | undefined;
  denominatorValue: number | null | undefined;
  onNumeratorChange: (value: number | null) => void;
  onDenominatorChange: (value: number | null) => void;
  disabled?: boolean;
  numeratorLabel?: string;
  denominatorLabel?: string;
}) {
  const rate = numeratorValue != null && denominatorValue != null && denominatorValue > 0
    ? ((numeratorValue / denominatorValue) * 100).toFixed(2)
    : null;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className="text-sm text-primary font-medium">{numeratorLabel}</Label>
          <Input
            type="number"
            min={0}
            placeholder="Enter count"
            value={numeratorValue ?? ''}
            onChange={(e) => onNumeratorChange(e.target.value ? parseInt(e.target.value, 10) : null)}
            disabled={disabled}
            className="mt-1"
          />
        </div>
        <div>
          <Label className="text-sm text-primary font-medium">{denominatorLabel}</Label>
          <Input
            type="number"
            min={1}
            placeholder="Enter count"
            value={denominatorValue ?? ''}
            onChange={(e) => onDenominatorChange(e.target.value ? parseInt(e.target.value, 10) : null)}
            disabled={disabled}
            className="mt-1"
          />
        </div>
      </div>
      {rate && (
        <div className="bg-slate-100 rounded-md px-3 py-2 inline-flex items-center gap-2">
          <span className="text-sm font-medium text-slate-700">Performance Rate:</span>
          <span className="text-sm font-bold text-emerald-600">{rate}%</span>
        </div>
      )}
    </div>
  );
}

/**
 * Score Pill Component
 * Design specs:
 * - Rounded pill (border-radius: 9999px)
 * - Height: 28px, Horizontal padding: 12px
 * - Border: 1px solid #E5E7EB
 * - Left segment: current score, Right segment: max score
 * 
 * Interaction Rules:
 * A. Completed (perfect score): Entire pill green tint, green text
 * B. Partial progress: Entire pill yellow/amber tint, amber text
 * C. Zero score: Entire pill gray tint, gray text
 */
function ScorePill({ points, maxPoints }: { points: number; maxPoints: number }) {
  const isComplete = points >= maxPoints && maxPoints > 0;
  const isZero = points === 0;

  // Completed state: entire pill green
  if (isComplete) {
    return (
      <div 
        className="inline-flex items-center h-7 rounded-full border overflow-hidden"
        style={{ backgroundColor: '#ECFDF5', borderColor: '#A7F3D0' }}
      >
        <span 
          className="px-3 text-sm font-semibold"
          style={{ color: '#027A48' }}
        >
          {points}
        </span>
        <span 
          className="px-3 text-sm font-semibold border-l"
          style={{ color: '#027A48', borderColor: '#A7F3D0' }}
        >
          {maxPoints}
        </span>
      </div>
    );
  }

  // Zero state: entire pill gray
  if (isZero) {
    return (
      <div 
        className="inline-flex items-center h-7 rounded-full border overflow-hidden"
        style={{ backgroundColor: '#F2F4F7', borderColor: '#E5E7EB' }}
      >
        <span 
          className="px-3 text-sm font-semibold"
          style={{ color: '#667085' }}
        >
          {points}
        </span>
        <span 
          className="px-3 text-sm font-semibold border-l"
          style={{ color: '#667085', borderColor: '#D0D7E2' }}
        >
          {maxPoints}
        </span>
      </div>
    );
  }

  // Partial progress: entire pill yellow/amber (subtle, like the green variant)
  return (
    <div 
      className="inline-flex items-center h-7 rounded-full border overflow-hidden"
      style={{ backgroundColor: '#FFFBEB', borderColor: '#FDE68A' }}
    >
      <span 
        className="px-3 text-sm font-semibold"
        style={{ color: '#B45309' }}
      >
        {Number.isInteger(points) ? points : points.toFixed(2)}
      </span>
      <span 
        className="px-3 text-sm font-semibold border-l"
        style={{ color: '#B45309', borderColor: '#FDE68A' }}
      >
        {maxPoints}
      </span>
    </div>
  );
}

/**
 * Get the border color class based on status
 */
function getStatusBorderColor(status?: 'complete' | 'incomplete' | 'failed'): string {
  switch (status) {
    case 'complete':
      return 'border-l-4 border-l-emerald-500';
    case 'failed':
      return 'border-l-4 border-l-destructive';
    case 'incomplete':
    default:
      return '';
  }
}


/**
 * Section header with title, subtitle, score pill, and status icon
 * Design matches IA Attestation Cards:
 * - Chevron on the LEFT (toggles based on parent's data-state via CSS)
 * - Status icon next to title
 * - Score pill and status badge on the RIGHT
 */
function SectionHeader({
  number,
  title,
  subtitle,
  points,
  maxPoints,
  status,
}: {
  number: number;
  title: string;
  subtitle: string;
  points?: number;
  maxPoints?: number;
  status?: 'complete' | 'incomplete' | 'failed';
}) {
  const hasBadge = points !== undefined && maxPoints !== undefined;

  return (
    <div className="flex items-center justify-between w-full">
      {/* Left cluster: Chevron + Icon + Title/Subtitle */}
      <div className="flex items-center gap-3">
        {/* Chevron - Toggles based on accordion state using group-data */}
        <div className="flex-shrink-0">
          <ChevronRight className="w-5 h-5 text-gray-400 group-data-[state=open]:hidden" />
          <ChevronDown className="w-5 h-5 text-gray-400 hidden group-data-[state=open]:block" />
        </div>

        {/* Status icon - 20px, vertically centered */}
        <div className="flex-shrink-0">
          {status === 'complete' && <CheckCircle2 className="w-5 h-5 text-emerald-500" />}
          {status === 'incomplete' && <div className="w-5 h-5 rounded-full border-2 border-gray-300" />}
          {status === 'failed' && <XCircle className="w-5 h-5 text-destructive" />}
        </div>

        {/* Title + Subtitle stacked */}
        <div className="text-left">
          <div className="text-base font-medium text-slate-900">{number}. {title}</div>
          <div className="text-sm text-slate-500 mt-0.5">{subtitle}</div>
        </div>
      </div>

      {/* Right cluster: Score pill only */}
      {hasBadge && (
        <div className="flex-shrink-0">
          <ScorePill points={points} maxPoints={maxPoints} />
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Section 1: Reporting Context
// ============================================================================

export function ReportingContextSection({ data, onChange, disabled }: SectionProps) {
  const { programYear } = useProgramYear();
  const validatePeriod = () => {
    if (!data.performance_start || !data.performance_end) return null;
    const start = new Date(data.performance_start);
    const end = new Date(data.performance_end);
    const diffDays = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
    return diffDays >= 180;
  };

  const periodValid = validatePeriod();
  // Complete only when dates are valid AND CEHRT ID is entered
  const isComplete = data.performance_start && data.performance_end && periodValid !== false && data.cehrt_id && data.cehrt_id.trim().length > 0;
  const status = isComplete ? 'complete' : 'incomplete' as const;

  return (
    <AccordionItem value="reporting-context" className={`border rounded-lg bg-card transition-all ${getStatusBorderColor(status)}`}>
      <AccordionTrigger className="px-5 py-5 hover:no-underline [&>svg]:hidden hover:bg-slate-50/50 items-center text-left min-h-[88px] group">
        <SectionHeader
          number={1}
          title="Reporting Details"
          subtitle="Enter performance period and CEHRT certification."
          status={status}
        />
      </AccordionTrigger>
      <AccordionContent className="px-4 pb-4 pt-4 border-t space-y-6">
        {/* Performance Period */}
        <div className="space-y-4">
          <DateRangePicker
            startDate={data.performance_start}
            endDate={data.performance_end}
            onStartDateChange={(date) => onChange({ performance_start: date ? toLocalDateString(date) : null })}
            onEndDateChange={(date) => onChange({ performance_end: date ? toLocalDateString(date) : null })}
            minDate={new Date(programYear, 0, 1)}
            maxStartDate={new Date(programYear, 6, 5)}
            maxEndDate={new Date(programYear, 11, 31)}
            disabled={disabled}
          />
          <p className="text-xs text-muted-foreground">Performance period must be at least 180 consecutive days. Latest start date: July 5, {programYear}.</p>

          {periodValid === false && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                Performance period must be at least 180 consecutive days.
              </AlertDescription>
            </Alert>
          )}
        </div>

        {/* CEHRT ID */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label className="text-sm font-medium text-gray-900">CMS EHR Certification ID</Label>
            <a 
              href="https://chpl.healthit.gov" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-sm font-semibold text-primary hover:text-primary/80 flex items-center gap-1"
            >
              Find your ID on CHPL
              <ExternalLink className="w-3 h-3" />
            </a>
          </div>
          <Input
            value={data.cehrt_id || ''}
            onChange={(e) => onChange({ cehrt_id: e.target.value || null })}
            disabled={disabled}
            placeholder="15-character alphanumeric"
            maxLength={20}
          />
          <p className="text-xs text-muted-foreground">
            Your CMS EHR Certification ID can be found on the ONC Certified Health IT Product List (CHPL). This ID is required for CMS submission.
          </p>
        </div>

        <InfoBox>
          For {programYear}, the PI performance period must be a minimum of 180 consecutive days. If reporting as a Group, all data entered will apply to the entire TIN. Select 'Individual NPI' at the top of the screen if you need to enter separate data for a specific clinician.
        </InfoBox>
      </AccordionContent>
    </AccordionItem>
  );
}

// ============================================================================
// Section 2: Prerequisite Attestations
// ============================================================================

export function PrerequisitesSection({ data, onChange, disabled }: SectionProps) {
  const { programYear } = useProgramYear();
  const allYes = 
    data.pi_pphi_1 === true &&
    data.pi_pphi_2 === true &&
    data.pi_infblo_1 === true &&
    data.pi_oncdir_1 === true;

  const anyNo = 
    data.pi_pphi_1 === false ||
    data.pi_pphi_2 === false ||
    data.pi_infblo_1 === false ||
    data.pi_oncdir_1 === false;

  const status = allYes ? 'complete' : anyNo ? 'failed' : 'incomplete' as const;

  return (
    <AccordionItem value="prerequisites" className={`border rounded-lg bg-card transition-all ${getStatusBorderColor(status)}`}>
      <AccordionTrigger className="px-5 py-5 hover:no-underline [&>svg]:hidden hover:bg-slate-50/50 items-center text-left min-h-[88px] group">
        <SectionHeader
          number={2}
          title="Prerequisite Attestations"
          subtitle="Complete required attestations (all must be Yes)."
          status={status}
        />
      </AccordionTrigger>
      <AccordionContent className="px-4 pb-4 pt-4 border-t space-y-6">
        {anyNo && (
          <Alert variant="destructive">
            <XCircle className="h-4 w-4" />
            <AlertTitle>Prerequisites Not Met</AlertTitle>
            <AlertDescription>
              One or more prerequisite attestations is 'No'. Your PI category score will be 0 points. All subsequent sections are disabled until all prerequisites are answered 'Yes'.
            </AlertDescription>
          </Alert>
        )}

        {/* Security Risk Analysis */}
        <div className="space-y-3 p-4 rounded-lg border">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <span className="font-semibold">Security Risk Analysis</span>
                <span className="text-muted-foreground">(</span>
                <MeasureIdLink measureId="PI_PPHI_1" />
                <span className="text-muted-foreground">)</span>
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                Did you conduct or review a security risk analysis during the {programYear} calendar year?
              </p>
            </div>
            <YesNoButtonGroup
              value={data.pi_pphi_1}
              onChange={(v) => onChange({ pi_pphi_1: v })}
              disabled={disabled}
            />
          </div>
          <InfoBox>
            The Security Risk Analysis must be unique to {programYear} and assess risks to all electronic protected health information (ePHI) created or maintained by your certified EHR. It must address encryption requirements and include a plan to correct any identified deficiencies. Simply reviewing a prior year's SRA does not satisfy this requirement.
          </InfoBox>
        </div>

        {/* SAFER Guide Assessment */}
        <div className="space-y-3 p-4 rounded-lg border">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <span className="font-semibold">SAFER Guide Assessment</span>
                <span className="text-muted-foreground">(</span>
                <MeasureIdLink measureId="PI_PPHI_2" />
                <span className="text-muted-foreground">)</span>
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                Did you complete the High Priority Practices section of ONC's SAFER Guides during the {programYear} calendar year?
              </p>
            </div>
            <YesNoButtonGroup
              value={data.pi_pphi_2}
              onChange={(v) => onChange({ pi_pphi_2: v })}
              disabled={disabled}
            />
          </div>
          <InfoBox>
            You must complete an annual self-assessment using ONC's Safety Assurance Factors for EHR Resilience (SAFER) Guides. All checkboxes must be marked (fully implemented, partially implemented, or not implemented). Full implementation is not required—only completion of the assessment.
          </InfoBox>
        </div>

        {/* No Interoperability Restrictions */}
        <div className="space-y-3 p-4 rounded-lg border">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <span className="font-semibold">No Interoperability Restrictions</span>
                <span className="text-muted-foreground">(</span>
                <MeasureIdLink measureId="PI_INFBLO_1" />
                <span className="text-muted-foreground">)</span>
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                Do you attest that you have not knowingly and willfully taken action to limit or restrict the compatibility or interoperability of your certified EHR technology?
              </p>
            </div>
            <YesNoButtonGroup
              value={data.pi_infblo_1}
              onChange={(v) => onChange({ pi_infblo_1: v })}
              disabled={disabled}
            />
          </div>
          <InfoBox>
            This attestation confirms you have not engaged in information blocking as defined under the 21st Century Cures Act. Responding 'No' will result in zero points for the entire PI category.
          </InfoBox>
        </div>

        {/* ONC Direct Review Acknowledgement */}
        <div className="space-y-3 p-4 rounded-lg border">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <span className="font-semibold">ONC Direct Review Acknowledgement</span>
                <span className="text-muted-foreground">(</span>
                <MeasureIdLink measureId="PI_ONCDIR_1" />
                <span className="text-muted-foreground">)</span>
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                Do you acknowledge that you may be subject to a Direct Review by ONC if selected, and that you will cooperate in good faith?
              </p>
            </div>
            <YesNoButtonGroup
              value={data.pi_oncdir_1}
              onChange={(v) => onChange({ pi_oncdir_1: v })}
              disabled={disabled}
            />
          </div>
        </div>
      </AccordionContent>
    </AccordionItem>
  );
}

// ============================================================================
// Section 3: e-Prescribing
// ============================================================================

interface EPrescribingSectionProps extends SectionProps {
  breakdown: PIScoreBreakdown;
  queryPdmpBreakdown: PIScoreBreakdown;
}

export function EPrescribingSection({ 
  data, 
  onChange, 
  disabled,
  breakdown,
  queryPdmpBreakdown,
}: EPrescribingSectionProps) {
  const prerequisitesFailed = !(
    data.pi_pphi_1 === true &&
    data.pi_pphi_2 === true &&
    data.pi_infblo_1 === true &&
    data.pi_oncdir_1 === true
  );

  const isDisabled = disabled || prerequisitesFailed;
  const totalPoints = breakdown.points + queryPdmpBreakdown.points;
  const totalMax = breakdown.maxPoints + queryPdmpBreakdown.maxPoints;
  const status = prerequisitesFailed ? 'incomplete' : totalPoints > 0 ? 'complete' : 'incomplete' as const;

  return (
    <AccordionItem value="e-prescribing" className={`border rounded-lg bg-card transition-all ${getStatusBorderColor(status)}`}>
      <AccordionTrigger className="px-5 py-5 hover:no-underline [&>svg]:hidden hover:bg-slate-50/50 items-center text-left min-h-[88px] group">
        <SectionHeader
          number={3}
          title="e-Prescribing Objective"
          subtitle="Report performance for e-prescribing measures (20 points maximum)."
          points={totalPoints}
          maxPoints={totalMax}
          status={status}
        />
      </AccordionTrigger>
      <AccordionContent className="px-4 pb-4 pt-4 border-t space-y-6">
        {prerequisitesFailed && (
          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              Complete all prerequisite attestations to enable this section.
            </AlertDescription>
          </Alert>
        )}

        {/* e-Prescribing Measure */}
        <div className="space-y-4 p-4 border border-slate-200 rounded-lg">
          <h4 className="text-base font-semibold text-gray-900">
            e-Prescribing (<MeasureIdLink measureId="PI_EP_1" />) — 10 points
          </h4>

          {/* Claim Exclusion */}
          <div className="flex items-center justify-between p-4 bg-slate-50 border border-slate-200 rounded-lg">
            <div>
              <div className="font-medium text-gray-900">
                Claim Exclusion (<MeasureIdLink measureId="PI_LVPP_1" />)
              </div>
              <p className="text-sm text-gray-600">
                Wrote fewer than 100 permissible prescriptions during the performance period
              </p>
            </div>
            <YesNoButtonGroup
              value={data.pi_lvpp_1}
              onChange={(v) => onChange({ pi_lvpp_1: v })}
              disabled={isDisabled}
            />
          </div>

          {!data.pi_lvpp_1 && (
            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-1.5">
                <Label className="text-sm font-medium text-gray-900">Numerator: Prescriptions transmitted electronically</Label>
                <Input
                  type="number"
                  min={0}
                  placeholder="Enter count"
                  value={data.pi_ep_1_numerator ?? ''}
                  onChange={(e) => {
                    const val = e.target.value ? Math.max(0, parseInt(e.target.value, 10)) : null;
                    // Validate: numerator cannot exceed denominator
                    if (val !== null && data.pi_ep_1_denominator != null && val > data.pi_ep_1_denominator) {
                      onChange({ pi_ep_1_numerator: data.pi_ep_1_denominator });
                    } else {
                      onChange({ pi_ep_1_numerator: val });
                    }
                  }}
                  disabled={isDisabled}
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm font-medium text-gray-900">Denominator: Total permissible prescriptions written</Label>
                <Input
                  type="number"
                  min={1}
                  placeholder="Enter count"
                  value={data.pi_ep_1_denominator ?? ''}
                  onChange={(e) => onChange({ pi_ep_1_denominator: e.target.value ? Math.max(1, parseInt(e.target.value, 10)) : null })}
                  disabled={isDisabled}
                />
              </div>
            </div>
          )}

          {!data.pi_lvpp_1 && data.pi_ep_1_numerator != null && data.pi_ep_1_denominator != null && data.pi_ep_1_denominator > 0 && (
            <div className="bg-slate-100 rounded-md px-4 py-3 flex items-center gap-2">
              <span className="text-sm font-medium text-gray-700">Performance Rate:</span>
              <span className="text-base font-bold text-primary">
                {Math.min(100, Math.max(0, (data.pi_ep_1_numerator / data.pi_ep_1_denominator) * 100)).toFixed(2)}%
              </span>
            </div>
          )}

          <InfoBox>
            Include all prescriptions written for drugs requiring a prescription (other than controlled substances). You may include controlled substances if done uniformly for your entire performance period.
          </InfoBox>
        </div>

        {/* Query of PDMP */}
        <div className="space-y-4 p-4 border border-slate-200 rounded-lg">
          <h4 className="text-base font-semibold text-gray-900">
            Query of PDMP (<MeasureIdLink measureId="PI_EP_2" />) — 10 points
          </h4>

          <div className="space-y-2">
            <Label className="text-sm font-medium text-gray-900">Exclusion Type</Label>
            <Select
              value={data.pi_ep_2_exclusion || 'none'}
              onValueChange={(v) => onChange({ 
                pi_ep_2_exclusion: v === 'none' ? null : v as 'EX_1' | 'EX_2',
                pi_ep_2: v === 'none' ? data.pi_ep_2 : null
              })}
              disabled={isDisabled}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select exclusion (if applicable)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None - No exclusion</SelectItem>
                <SelectItem value="EX_1">Unable to electronically prescribe Schedule II-IV drugs</SelectItem>
                <SelectItem value="EX_2">Did not prescribe any Schedule II-IV drugs</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {!data.pi_ep_2_exclusion && (
            <div className="flex items-center justify-between p-4 bg-slate-50 border border-slate-200 rounded-lg">
              <div className="flex-1 mr-4">
                <div className="font-medium text-gray-900">Query of PDMP Attestation</div>
                <p className="text-sm text-gray-600">
                  Did you use CEHRT data to query a PDMP for prescription drug history for at least one Schedule II opioid or Schedule III/IV drug that was electronically prescribed?
                </p>
              </div>
              <YesNoButtonGroup
                value={data.pi_ep_2}
                onChange={(v) => onChange({ pi_ep_2: v })}
                disabled={isDisabled}
              />
            </div>
          )}

          <InfoBox>
            This is an all-or-nothing measure. Answering 'Yes' awards the full 10 points. Answering 'No' results in 0 points for this measure.
          </InfoBox>

          {data.pi_lvpp_1 && !data.pi_ep_2_exclusion && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                If claiming e-Prescribing exclusion, you must also claim a Query of PDMP exclusion.
              </AlertDescription>
            </Alert>
          )}
        </div>

        <PointsEarnedFooter points={totalPoints} maxPoints={totalMax} />
      </AccordionContent>
    </AccordionItem>
  );
}

// ============================================================================
// Section 4: Provider to Patient Exchange
// ============================================================================

interface PatientAccessSectionProps extends SectionProps {
  breakdown: PIScoreBreakdown;
}

export function PatientAccessSection({ 
  data, 
  onChange, 
  disabled,
  breakdown,
}: PatientAccessSectionProps) {
  const prerequisitesFailed = !(
    data.pi_pphi_1 === true &&
    data.pi_pphi_2 === true &&
    data.pi_infblo_1 === true &&
    data.pi_oncdir_1 === true
  );

  const isDisabled = disabled || prerequisitesFailed;
  const status = prerequisitesFailed ? 'incomplete' : breakdown.points > 0 ? 'complete' : 'incomplete' as const;

  return (
    <AccordionItem value="patient-access" className={`border rounded-lg bg-card transition-all ${getStatusBorderColor(status)}`}>
      <AccordionTrigger className="px-5 py-5 hover:no-underline [&>svg]:hidden hover:bg-slate-50/50 items-center text-left min-h-[88px] group">
        <SectionHeader
          number={4}
          title="Provider-to-Patient Exchange Objective"
          subtitle="Submit data for secure patient access measures (25 points maximum)."
          points={breakdown.points}
          maxPoints={breakdown.maxPoints}
          status={status}
        />
      </AccordionTrigger>
      <AccordionContent className="px-4 pb-4 pt-4 border-t space-y-6">
        {prerequisitesFailed && (
          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              Complete all prerequisite attestations to enable this section.
            </AlertDescription>
          </Alert>
        )}

        <div className="space-y-4">
          <h4 className="text-base font-semibold text-gray-900">
            Provide Patients Electronic Access to Their Health Information (<MeasureIdLink measureId="PI_PEA_1" />)
          </h4>

          <Alert className="bg-amber-50 border-amber-200">
            <AlertTriangle className="h-4 w-4 text-amber-600" />
            <AlertTitle className="text-amber-800">No Exclusion Available</AlertTitle>
            <AlertDescription className="text-amber-700">
              This measure cannot be excluded. Points may be redistributed TO this measure from other excluded measures, but it cannot itself be excluded.
            </AlertDescription>
          </Alert>

          <div className="grid grid-cols-2 gap-6">
            <div className="space-y-1.5">
              <Label className="text-sm font-medium text-gray-900">Numerator: Patients provided timely access (within 4 business days)</Label>
              <Input
                type="number"
                min={0}
                placeholder="Enter count"
                value={data.pi_pea_1_numerator ?? ''}
                onChange={(e) => {
                  const val = e.target.value ? Math.max(0, parseInt(e.target.value, 10)) : null;
                  if (val !== null && data.pi_pea_1_denominator != null && val > data.pi_pea_1_denominator) {
                    onChange({ pi_pea_1_numerator: data.pi_pea_1_denominator });
                  } else {
                    onChange({ pi_pea_1_numerator: val });
                  }
                }}
                disabled={isDisabled}
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm font-medium text-gray-900">Denominator: Total unique patients seen during performance period</Label>
              <Input
                type="number"
                min={1}
                placeholder="Enter count"
                value={data.pi_pea_1_denominator ?? ''}
                onChange={(e) => onChange({ pi_pea_1_denominator: e.target.value ? Math.max(1, parseInt(e.target.value, 10)) : null })}
                disabled={isDisabled}
              />
            </div>
          </div>

          <InfoBox>
            Patients must be provided BOTH: (1) the ability to view online, download, and transmit their health information, AND (2) access through an API-enabled application of their choice. Count each patient only once regardless of number of visits. Patients who opt out of electronic access may still be counted in the numerator if they were provided all the information necessary to access their data and chose not to use it.
          </InfoBox>
        </div>

        <PointsEarnedFooter points={breakdown.points} maxPoints={breakdown.maxPoints} />
      </AccordionContent>
    </AccordionItem>
  );
}

// ============================================================================
// Section 5: Health Information Exchange
// ============================================================================

interface HIESectionProps extends SectionProps {
  breakdown: PIScoreBreakdown;
}

export function HIESection({ 
  data, 
  onChange, 
  disabled,
  breakdown,
}: HIESectionProps) {
  const prerequisitesFailed = !(
    data.pi_pphi_1 === true &&
    data.pi_pphi_2 === true &&
    data.pi_infblo_1 === true &&
    data.pi_oncdir_1 === true
  );

  const isDisabled = disabled || prerequisitesFailed;
  const status = prerequisitesFailed ? 'incomplete' : breakdown.points > 0 ? 'complete' : 'incomplete' as const;

  return (
    <AccordionItem value="hie" className={`border rounded-lg bg-card transition-all ${getStatusBorderColor(status)}`}>
      <AccordionTrigger className="px-5 py-5 hover:no-underline [&>svg]:hidden hover:bg-slate-50/50 items-center text-left min-h-[88px] group">
        <SectionHeader
          number={5}
          title="Health Information Exchange Objective"
          subtitle="Select and report the HIE measure(s) that apply (30 points maximum)."
          points={breakdown.points}
          maxPoints={breakdown.maxPoints}
          status={status}
        />
      </AccordionTrigger>
      <AccordionContent className="px-4 pb-4 pt-4 border-t space-y-6">
        {prerequisitesFailed && (
          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              Complete all prerequisite attestations to enable this section.
            </AlertDescription>
          </Alert>
        )}

        {/* HIE Option Selection */}
        <div className="space-y-3">
          <Label className="text-sm font-medium text-gray-900">Select HIE Reporting Option</Label>
          <RadioGroup
            value={data.hie_option?.toString() || ''}
            onValueChange={(v) => onChange({ 
              hie_option: parseInt(v, 10) as 1 | 2 | 3,
              pi_hie_5: v === '2' ? data.pi_hie_5 : null,
              pi_hie_6: v === '3' ? data.pi_hie_6 : null,
            })}
            disabled={isDisabled}
            className="space-y-3"
          >
            <div className={`flex items-start space-x-3 p-4 rounded-lg border cursor-pointer ${data.hie_option === 1 ? 'border-primary bg-primary/5' : 'hover:bg-muted/50'}`}>
              <RadioGroupItem value="1" id="hie-1" className="mt-0.5" />
              <div>
                <Label htmlFor="hie-1" className="cursor-pointer">
                  <span className="text-sm text-muted-foreground">Option 1:</span>{' '}
                  <span className="font-semibold text-gray-900">Traditional HIE Measures</span>{' '}
                  <span className="text-gray-600">(Electronic Referral Loops)</span>
                </Label>
                <p className="text-sm text-muted-foreground mt-1">
                  <MeasureIdLink measureId="PI_HIE_1" /> (15 pts) + <MeasureIdLink measureId="PI_HIE_4" /> (15 pts)
                </p>
              </div>
            </div>
            <div className={`flex items-start space-x-3 p-4 rounded-lg border cursor-pointer ${data.hie_option === 2 ? 'border-primary bg-primary/5' : 'hover:bg-muted/50'}`}>
              <RadioGroupItem value="2" id="hie-2" className="mt-0.5" />
              <div>
                <Label htmlFor="hie-2" className="cursor-pointer">
                  <span className="text-sm text-muted-foreground">Option 2:</span>{' '}
                  <span className="font-semibold text-gray-900">HIE Bi-Directional Exchange</span>
                </Label>
                <p className="text-sm text-muted-foreground mt-1">
                  <MeasureIdLink measureId="PI_HIE_5" /> (30 pts attestation)
                </p>
              </div>
            </div>
            <div className={`flex items-start space-x-3 p-4 rounded-lg border cursor-pointer ${data.hie_option === 3 ? 'border-primary bg-primary/5' : 'hover:bg-muted/50'}`}>
              <RadioGroupItem value="3" id="hie-3" className="mt-0.5" />
              <div>
                <Label htmlFor="hie-3" className="cursor-pointer">
                  <span className="text-sm text-muted-foreground">Option 3:</span>{' '}
                  <span className="font-semibold text-gray-900">TEFCA Exchange</span>
                </Label>
                <p className="text-sm text-muted-foreground mt-1">
                  <MeasureIdLink measureId="PI_HIE_6" /> (30 pts attestation)
                </p>
              </div>
            </div>
          </RadioGroup>
        </div>

        {/* Option 1: Traditional HIE */}
        {data.hie_option === 1 && (
          <div className="space-y-6">
            {/* HIE Sending */}
            <div className="space-y-4 p-4 border border-slate-200 rounded-lg">
              <h4 className="text-base font-semibold text-gray-900">
                HIE Sending (<MeasureIdLink measureId="PI_HIE_1" />) — 15 points
              </h4>

              <div className="flex items-center justify-between p-4 bg-slate-50 border border-slate-200 rounded-lg">
                <div>
                  <div className="font-medium text-gray-900">
                    Claim Exclusion (<MeasureIdLink measureId="PI_LVOTC_1" />)
                  </div>
                  <p className="text-sm text-gray-600">
                    Fewer than 100 transitions/referrals during performance period
                  </p>
                </div>
                <YesNoButtonGroup
                  value={data.pi_lvotc_1}
                  onChange={(v) => onChange({ pi_lvotc_1: v })}
                  disabled={isDisabled}
                />
              </div>

              {!data.pi_lvotc_1 && (
                <div className="grid grid-cols-2 gap-6">
                  <div className="space-y-1.5">
                    <Label className="text-sm font-medium text-gray-900">Numerator: Transitions/referrals with electronic summary exchange</Label>
                    <Input
                      type="number"
                      min={0}
                      placeholder=""
                      value={data.pi_hie_1_numerator ?? ''}
                      onChange={(e) => {
                        const val = e.target.value ? Math.max(0, parseInt(e.target.value, 10)) : null;
                        if (val !== null && data.pi_hie_1_denominator != null && val > data.pi_hie_1_denominator) {
                          onChange({ pi_hie_1_numerator: data.pi_hie_1_denominator });
                        } else {
                          onChange({ pi_hie_1_numerator: val });
                        }
                      }}
                      disabled={isDisabled}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-sm font-medium text-gray-900">Denominator: Total transitions of care and referrals</Label>
                    <Input
                      type="number"
                      min={1}
                      placeholder=""
                      value={data.pi_hie_1_denominator ?? ''}
                      onChange={(e) => onChange({ pi_hie_1_denominator: e.target.value ? Math.max(1, parseInt(e.target.value, 10)) : null })}
                      disabled={isDisabled}
                    />
                  </div>
                </div>
              )}

              <InfoBox>
                'Exchanged electronically' requires reasonable certainty of receipt by the receiving provider. This can be confirmed through a read receipt, query confirmation, or acknowledgment.
              </InfoBox>
            </div>

            {/* HIE Receiving and Reconciling */}
            <div className="space-y-4 p-4 border border-slate-200 rounded-lg">
              <h4 className="text-base font-semibold text-gray-900">
                HIE Receiving and Reconciling (<MeasureIdLink measureId="PI_HIE_4" />) — 15 points
              </h4>

              <div className="flex items-center justify-between p-4 bg-slate-50 border border-slate-200 rounded-lg">
                <div>
                  <div className="font-medium text-gray-900">
                    Claim Exclusion (<MeasureIdLink measureId="PI_LVITC_2" />)
                  </div>
                  <p className="text-sm text-gray-600">
                    Received fewer than 100 transitions/referrals OR new patients
                  </p>
                </div>
                <YesNoButtonGroup
                  value={data.pi_lvitc_2}
                  onChange={(v) => onChange({ pi_lvitc_2: v })}
                  disabled={isDisabled}
                />
              </div>

              {!data.pi_lvitc_2 && (
                <div className="grid grid-cols-2 gap-6">
                  <div className="space-y-1.5">
                    <Label className="text-sm font-medium text-gray-900">Numerator: Records with all three reconciled</Label>
                    <Input
                      type="number"
                      min={0}
                      placeholder=""
                      value={data.pi_hie_4_numerator ?? ''}
                      onChange={(e) => {
                        const val = e.target.value ? Math.max(0, parseInt(e.target.value, 10)) : null;
                        if (val !== null && data.pi_hie_4_denominator != null && val > data.pi_hie_4_denominator) {
                          onChange({ pi_hie_4_numerator: data.pi_hie_4_denominator });
                        } else {
                          onChange({ pi_hie_4_numerator: val });
                        }
                      }}
                      disabled={isDisabled}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-sm font-medium text-gray-900">Denominator: Electronic summary records received</Label>
                    <Input
                      type="number"
                      min={1}
                      placeholder=""
                      value={data.pi_hie_4_denominator ?? ''}
                      onChange={(e) => onChange({ pi_hie_4_denominator: e.target.value ? Math.max(1, parseInt(e.target.value, 10)) : null })}
                      disabled={isDisabled}
                    />
                  </div>
                </div>
              )}

              <InfoBox>
                Reconciliation must include ALL THREE: (1) Medications, (2) Medication allergies, (3) Current problem list. Reconciliation may consist of verifying no update is necessary.
              </InfoBox>
            </div>
          </div>
        )}

        {/* Options 2 and 3 info box */}
        {(data.hie_option === 2 || data.hie_option === 3) && (
          <Alert className="bg-blue-50 border-blue-200">
            <Info className="h-4 w-4 text-blue-600" />
            <AlertDescription className="text-blue-700">
              Options 2 and 3 are attestation-only measures. No exclusions are available.
            </AlertDescription>
          </Alert>
        )}

        {/* Option 2: Bi-Directional Exchange */}
        {data.hie_option === 2 && (
          <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex-1 mr-4">
                <div className="font-medium text-gray-900">
                  HIE Bi-Directional Exchange Attestation (<MeasureIdLink measureId="PI_HIE_5" />)
                </div>
                <p className="text-sm text-gray-600">
                  Are you engaged with an HIE that enables secure, bi-directional exchange of information for every patient encounter during the performance period?
                </p>
              </div>
              <YesNoButtonGroup
                value={data.pi_hie_5}
                onChange={(v) => onChange({ pi_hie_5: v })}
                disabled={isDisabled}
              />
            </div>
            <div className="text-sm text-gray-600 pt-2 border-t border-slate-200">
              The HIE must not be limited to affiliated entities, single-vendor networks, or EHRs of a specific brand. The exchange must be truly bi-directional—both sending and receiving.
            </div>
          </div>
        )}

        {/* Option 3: TEFCA */}
        {data.hie_option === 3 && (
          <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex-1 mr-4">
                <div className="font-medium text-gray-900">
                  TEFCA Exchange Attestation (<MeasureIdLink measureId="PI_HIE_6" />)
                </div>
                <p className="text-sm text-gray-600">
                  Are you a signatory in good standing to a TEFCA Framework Agreement with bi-directional exchange capabilities in production?
                </p>
              </div>
              <YesNoButtonGroup
                value={data.pi_hie_6}
                onChange={(v) => onChange({ pi_hie_6: v })}
                disabled={isDisabled}
              />
            </div>
            <div className="text-sm text-gray-600 pt-2 border-t border-slate-200">
              TEFCA = Trusted Exchange Framework and Common Agreement. This is a newer, streamlined option for practices already participating in TEFCA networks.
            </div>
          </div>
        )}

        <PointsEarnedFooter points={breakdown.points} maxPoints={breakdown.maxPoints} />
      </AccordionContent>
    </AccordionItem>
  );
}

// ============================================================================
// Section 6: Public Health & Clinical Data Exchange
// ============================================================================

interface PublicHealthSectionProps extends SectionProps {
  breakdown: PIScoreBreakdown;
  bonusBreakdown: PIScoreBreakdown;
}

export function PublicHealthSection({ 
  data, 
  onChange, 
  disabled,
  breakdown,
  bonusBreakdown,
}: PublicHealthSectionProps) {
  const prerequisitesFailed = !(
    data.pi_pphi_1 === true &&
    data.pi_pphi_2 === true &&
    data.pi_infblo_1 === true &&
    data.pi_oncdir_1 === true
  );

  const isDisabled = disabled || prerequisitesFailed;
  const totalPoints = breakdown.points + bonusBreakdown.points;
  const status = prerequisitesFailed ? 'incomplete' : totalPoints > 0 ? 'complete' : 'incomplete' as const;

  return (
    <AccordionItem value="public-health" className={`border rounded-lg bg-card transition-all ${getStatusBorderColor(status)}`}>
      <AccordionTrigger className="px-5 py-5 hover:no-underline [&>svg]:hidden hover:bg-slate-50/50 items-center text-left min-h-[88px] group">
        <SectionHeader
          number={6}
          title="Public Health & Clinical Data Exchange Objective"
          subtitle="Report required measure and optional bonus measures (25 + 5 points)."
          points={totalPoints}
          maxPoints={breakdown.maxPoints + bonusBreakdown.maxPoints}
          status={status}
        />
      </AccordionTrigger>
      <AccordionContent className="px-4 pb-4 pt-4 border-t space-y-6">
        {prerequisitesFailed && (
          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              Complete all prerequisite attestations to enable this section.
            </AlertDescription>
          </Alert>
        )}

        {/* Immunization Registry (Required) */}
        <div className="space-y-4 p-4 border border-slate-200 rounded-lg">
          <div className="flex items-center gap-3">
            <h4 className="text-base font-semibold text-gray-900">
              Immunization Registry Reporting (<MeasureIdLink measureId="PI_PHCDRR_1" />)
            </h4>
            <Badge className="bg-orange-100 text-orange-700 border-orange-200">Required — 25 points</Badge>
          </div>

          <div className="space-y-2">
            <Label className="text-sm font-medium text-gray-900">Exclusion (if applicable)</Label>
            <Select
              value={data.pi_phcdrr_1_exclusion || 'none'}
              onValueChange={(v) => onChange({ 
                pi_phcdrr_1_exclusion: v === 'none' ? null : v as 'EX_1' | 'EX_2' | 'EX_3',
                pi_phcdrr_1: v === 'none' ? data.pi_phcdrr_1 : null,
                pi_phcdrr_1_level: v === 'none' ? data.pi_phcdrr_1_level : null,
              })}
              disabled={isDisabled}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select an option" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No exclusion</SelectItem>
                <SelectItem value="EX_1">Does not diagnose/treat conditions associated with this registry</SelectItem>
                <SelectItem value="EX_2">No registry can accept electronic data in required standards</SelectItem>
                <SelectItem value="EX_3">Registry not declared readiness 6+ months before performance period</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {!data.pi_phcdrr_1_exclusion && (
            <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex-1 mr-4">
                  <div className="font-medium text-gray-900">Active Engagement Attestation</div>
                  <p className="text-sm text-gray-600">
                    Are you actively engaged with a public health agency to submit immunization data?
                  </p>
                </div>
                <YesNoButtonGroup
                  value={data.pi_phcdrr_1}
                  onChange={(v) => onChange({ pi_phcdrr_1: v })}
                  disabled={isDisabled}
                />
              </div>
              <div className="text-sm text-gray-600 pt-2 border-t border-slate-200">
                Active engagement requires at least completing registration with the immunization registry. Testing/validation or live production submission are preferred.
              </div>
            </div>
          )}
        </div>

        {/* Bonus Measures */}
        <div className="space-y-4 p-4 border border-slate-200 rounded-lg">
          <div className="flex items-center gap-3">
            <h4 className="text-base font-semibold text-gray-900">Optional Bonus Measures</h4>
            <Badge className="bg-green-100 text-green-700 border-green-200">Up to +5 points</Badge>
          </div>

          <InfoBox>
            A 'Yes' to ANY ONE of these measures awards 5 bonus points. Multiple 'Yes' responses still only award 5 points total.
          </InfoBox>

          {/* Syndromic Surveillance */}
          <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex-1 mr-4">
                <div className="font-medium text-gray-900">
                  Syndromic Surveillance (<MeasureIdLink measureId="PI_PHCDRR_2" />)
                </div>
                <p className="text-sm text-gray-600">
                  Are you actively engaged with a public health agency for syndromic surveillance?
                </p>
              </div>
              <YesNoButtonGroup
                value={data.pi_phcdrr_2}
                onChange={(v) => onChange({ pi_phcdrr_2: v })}
                disabled={isDisabled}
              />
            </div>
            {data.pi_phcdrr_2 === true && (
              <div className="space-y-2">
                <Label className="text-sm font-medium text-gray-900">Active Engagement Level</Label>
                <Select
                  value={data.pi_phcdrr_2_level || ''}
                  onValueChange={(v) => onChange({ pi_phcdrr_2_level: v || null })}
                  disabled={isDisabled}
                >
                  <SelectTrigger className="bg-white">
                    <SelectValue placeholder="Select engagement level" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="registration">Registration</SelectItem>
                    <SelectItem value="testing">Testing/Validation</SelectItem>
                    <SelectItem value="production">Live Production</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {/* Public Health Registry */}
          <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex-1 mr-4">
                <div className="font-medium text-gray-900">
                  Public Health Registry (<MeasureIdLink measureId="PI_PHCDRR_4" />)
                </div>
                <p className="text-sm text-gray-600">
                  Are you actively engaged with specialized disease registries (cancer, etc.)?
                </p>
              </div>
              <YesNoButtonGroup
                value={data.pi_phcdrr_4}
                onChange={(v) => onChange({ pi_phcdrr_4: v })}
                disabled={isDisabled}
              />
            </div>
            {data.pi_phcdrr_4 === true && (
              <div className="space-y-2">
                <Label className="text-sm font-medium text-gray-900">Active Engagement Level</Label>
                <Select
                  value={data.pi_phcdrr_4_level || ''}
                  onValueChange={(v) => onChange({ pi_phcdrr_4_level: v || null })}
                  disabled={isDisabled}
                >
                  <SelectTrigger className="bg-white">
                    <SelectValue placeholder="Select engagement level" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="registration">Registration</SelectItem>
                    <SelectItem value="testing">Testing/Validation</SelectItem>
                    <SelectItem value="production">Live Production</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {/* Clinical Data Registry */}
          <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex-1 mr-4">
                <div className="font-medium text-gray-900">
                  Clinical Data Registry (<MeasureIdLink measureId="PI_PHCDRR_5" />)
                </div>
                <p className="text-sm text-gray-600">
                  Are you actively engaged with clinical data registries?
                </p>
              </div>
              <YesNoButtonGroup
                value={data.pi_phcdrr_5}
                onChange={(v) => onChange({ pi_phcdrr_5: v })}
                disabled={isDisabled}
              />
            </div>
            {data.pi_phcdrr_5 === true && (
              <div className="space-y-2">
                <Label className="text-sm font-medium text-gray-900">Active Engagement Level</Label>
                <Select
                  value={data.pi_phcdrr_5_level || ''}
                  onValueChange={(v) => onChange({ pi_phcdrr_5_level: v || null })}
                  disabled={isDisabled}
                >
                  <SelectTrigger className="bg-white">
                    <SelectValue placeholder="Select engagement level" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="registration">Registration</SelectItem>
                    <SelectItem value="testing">Testing/Validation</SelectItem>
                    <SelectItem value="production">Live Production</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </div>

        <PointsEarnedFooter points={totalPoints} maxPoints={breakdown.maxPoints + bonusBreakdown.maxPoints} />
      </AccordionContent>
    </AccordionItem>
  );
}
